import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axiosSuperAdmin';
import DeleteConfirmModal from '../../components/DeleteConfirmModal';
import { toast } from '../../components/Toast';
import './SuperAdmin.css';

const PLANS = ['FREE', 'SILVER', 'GOLD', 'PLATINUM'];

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planModal, setPlanModal] = useState(null); // { id, name, currentPlan }
  const [selectedPlan, setSelectedPlan] = useState('FREE');
  const [viewModal, setViewModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: 20 });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`/superadmin/companies?${params}`);
      const data = res.data.data || res.data;
      setCompanies(data.content || data);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const showSuccess = (msg) => { toast.success(msg); };

  const handleSuspend = (id, name) => {
    setConfirmModal({
      title: 'Pause Company',
      message: `Are you sure you want to pause "${name}"? They will not be able to log in.`,
      confirmText: 'Pause',
      confirmClass: 'btn-warning',
      icon: '⏸',
      onConfirm: async () => {
        try {
          await axios.put(`/superadmin/companies/${id}/suspend`, { reason: 'Paused by super admin' });
          showSuccess('Company paused');
          fetchCompanies();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to pause');
        } finally {
          setConfirmModal(null);
        }
      }
    });
  };

  const handleReinstate = async (id) => {
    try {
      await axios.put(`/superadmin/companies/${id}/reinstate`);
      showSuccess('Company restarted');
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to restart'); }
  };

  const handleImpersonate = async (id) => {
    try {
      const res = await axios.post(`/superadmin/companies/${id}/impersonate`);
      const token = res.data.data?.impersonationToken || res.data.impersonationToken;
      if (token) {
        // Save the full super admin session before switching
        sessionStorage.setItem('impersonationToken', sessionStorage.getItem('token'));
        sessionStorage.setItem('impersonationUser', sessionStorage.getItem('user'));
        sessionStorage.setItem('impersonationPlanTier', sessionStorage.getItem('planTier') || '');
        sessionStorage.setItem('token', token);
        sessionStorage.removeItem('user'); // will be re-fetched on load
        window.location.href = '/';
      }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to impersonate'); }
  };

  const handleDelete = (id, name) => {
    setConfirmModal({
      title: 'Delete Company',
      message: `Are you sure you want to permanently delete "${name}"? This cannot be undone.`,
      confirmText: 'Delete',
      confirmClass: 'btn-danger',
      icon: '🗑️',
      onConfirm: async () => {
        try {
          await axios.delete(`/superadmin/companies/${id}`);
          showSuccess('Company deleted');
          fetchCompanies();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to delete');
        } finally {
          setConfirmModal(null);
        }
      }
    });
  };

  const openPlanModal = (company) => {
    setPlanModal({ id: company.id, name: company.name });
    setSelectedPlan(company.planTier || 'FREE');
  };

  const handleApprove = (id, name, requestedPlan) => {
    setConfirmModal({
      title: 'Approve Company',
      message: `Are you sure you want to approve "${name}"? They will be set to TRIAL status.`,
      confirmText: 'Approve',
      confirmClass: 'btn-success',
      icon: '✅',
      onConfirm: async () => {
        try {
          await axios.put(`/superadmin/companies/${id}/approve`, { plan: requestedPlan || 'FREE' });
          showSuccess(`${name} approved`);
          fetchCompanies();
        } catch (err) {
          toast.error(err.response?.data?.message || 'Failed to approve');
        } finally {
          setConfirmModal(null);
        }
      }
    });
  };

  const handleAssignPlan = async () => {
    try {
      await axios.put(`/superadmin/companies/${planModal.id}/subscription/assign-plan`, { plan: selectedPlan });
      showSuccess(`Plan set to ${selectedPlan} for ${planModal.name}`);
      setPlanModal(null);
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to assign plan'); }
  };

  return (
    <div className="sa-page">
      <h1 className="sa-title">Company Management</h1>

      <div className="sa-toolbar">
        <input className="sa-search" type="text" placeholder="Search companies..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
        <select className="sa-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
          <option value="">All Statuses</option>
          <option value="PENDING_APPROVAL">Pending Approval</option>
          <option value="TRIAL">Trial</option>
          <option value="ACTIVE">Active</option>
          <option value="SUSPENDED">Suspended</option>
          <option value="EXPIRED">Expired</option>
        </select>
      </div>

      {loading ? <div className="sa-loading">Loading...</div> : (
        <>
          <table className="sa-table">
            <thead>
              <tr><th>Company</th><th>Email</th><th>Plan</th><th>Status</th><th>Created</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {companies.length === 0 ? (
                <tr><td colSpan="6" className="sa-empty">No companies found</td></tr>
              ) : companies.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.adminEmail}</td>
                  <td>
                    <span className={`sa-badge sa-badge-${(c.planTier || 'free').toLowerCase()}`}>
                      {c.planTier || 'FREE'}
                    </span>
                  </td>
                  <td><span className={`sa-badge sa-badge-${c.status?.toLowerCase()}`}>{c.status}</span></td>
                  <td>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="sa-actions">
                    <button className="sa-btn sa-btn-sm sa-btn-info" onClick={() => setViewModal(c)} title="View Company Details">👁️ Details</button>
                    <button className="sa-btn sa-btn-sm" onClick={() => handleImpersonate(c.id)} title="Login as company">👤 Impersonated Login</button>
                    <button className="sa-btn sa-btn-sm sa-btn-primary" onClick={() => openPlanModal(c)} title="Assign plan">📦 Plan</button>
                    {c.status === 'PENDING_APPROVAL' && (
                      <button className="sa-btn sa-btn-sm sa-btn-success" onClick={() => handleApprove(c.id, c.name, c.planTier)}>✅ Approve</button>
                    )}
                    {c.status === 'SUSPENDED'
                      ? <button className="sa-btn sa-btn-sm sa-btn-success" onClick={() => handleReinstate(c.id)}>▶ Resume</button>
                      : c.status !== 'PENDING_APPROVAL' && <button className="sa-btn sa-btn-sm sa-btn-warning" onClick={() => handleSuspend(c.id, c.name)}>⏸ Pause</button>
                    }
                    <button className="sa-btn sa-btn-sm sa-btn-danger" onClick={() => handleDelete(c.id, c.name)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="sa-pagination">
              <button className="sa-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Previous</button>
              <span>Page {page + 1} of {totalPages}</span>
              <button className="sa-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next</button>
            </div>
          )}
        </>
      )}

      {/* Assign Plan Modal */}
      {planModal && (
        <div className="sa-modal-overlay" onClick={() => setPlanModal(null)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <h2>Assign Plan — {planModal.name}</h2>
            <p className="sa-hint">Select a plan to assign. This takes effect immediately and sets the company to Active.</p>
            <div className="sa-plan-options">
              {PLANS.map(p => (
                <div
                  key={p}
                  className={`sa-plan-option ${selectedPlan === p ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan(p)}
                >
                  {p}
                </div>
              ))}
            </div>
            <div className="sa-modal-actions">
              <button className="sa-btn" onClick={() => setPlanModal(null)}>Cancel</button>
              <button className="sa-btn sa-btn-primary" onClick={handleAssignPlan}>Assign {selectedPlan}</button>
            </div>
          </div>
        </div>
      )}

      {/* View Company Details Modal */}
      {viewModal && (
        <div className="sa-modal-overlay" onClick={() => setViewModal(null)}>
          <div className="sa-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2>Company Details</h2>
            <div className="sa-details-grid" style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '12px', margin: '20px 0', fontSize: '14px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
              <strong>Company ID:</strong>
              <span>{viewModal.id}</span>
              
              <strong>Company Name:</strong>
              <span>{viewModal.name}</span>
              
              <strong>Domain Slug:</strong>
              <span>{viewModal.slug}</span>
              
              <strong>Industry:</strong>
              <span>{viewModal.industry || '—'}</span>
              
              <strong>Country:</strong>
              <span>{viewModal.country || '—'}</span>
              
              <strong>Admin Email:</strong>
              <span>{viewModal.adminEmail}</span>
              
              <strong>Phone:</strong>
              <span>{viewModal.phone || '—'}</span>

              <strong>Alternate Phone:</strong>
              <span>{viewModal.alternatePhone || '—'}</span>

              <strong>Care Of (C/O):</strong>
              <span>{viewModal.careOf || '—'}</span>

              <strong>Second Contact Name:</strong>
              <span>{viewModal.secondContactName || '—'}</span>

              <strong>Second Contact Number:</strong>
              <span>{viewModal.secondContact || '—'}</span>

              <strong>Plan Tier:</strong>
              <span>
                <span className={`sa-badge sa-badge-${(viewModal.planTier || 'free').toLowerCase()}`} style={{ display: 'inline-block' }}>
                  {viewModal.planTier || 'FREE'}
                </span>
              </span>
              
              <strong>Status:</strong>
              <span>
                <span className={`sa-badge sa-badge-${viewModal.status?.toLowerCase()}`} style={{ display: 'inline-block' }}>
                  {viewModal.status}
                </span>
              </span>
              
              <strong>Created At:</strong>
              <span>{viewModal.createdAt ? new Date(viewModal.createdAt).toLocaleString() : '—'}</span>
              
              <strong>Updated At:</strong>
              <span>{viewModal.updatedAt ? new Date(viewModal.updatedAt).toLocaleString() : '—'}</span>
            </div>
            <div className="sa-modal-actions" style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
              <button className="sa-btn sa-btn-primary" onClick={() => setViewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={confirmModal !== null}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmText={confirmModal?.confirmText}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        onCancel={() => setConfirmModal(null)}
        onConfirm={confirmModal?.onConfirm}
      />
    </div>
  );
}

export default CompanyManagement;
